import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BaseSampleDetails, Batch, ContainerInfo} from 'constants/SampleConstants';
import {BatchInfo, BioentityInfo, BioentitySummary} from 'folder-items/FileConstants';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import preferences from 'Preferences';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import {entityIsContainable} from 'samples/containables/ContainableUtil';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import APIUtil from 'util/APIUtil';
import {WorkflowPreferences} from 'workflows/WorkflowConstants';

import PickContentsFromContainerTable from './PickContentsFromContainerTable';

export enum InputType {
  BATCH = 'BATCH',
  CONTAINER = 'CONTAINER',
  FILE = 'FILE',
}

export interface ExternalInputSpecifierType extends BaseSampleDetails {
  // True if batchInfo is present or bioentityInfo present and the bioentity is containable
  isContainable: boolean | null;
  containedItemsForContainer?: Array<BatchInfo | BioentityInfo> | null;
}

/**
 * Resolve the given specifier into an array of {batchId, containerId, fileId} samples.
 */
export function getInputSamplesFromSpecifier(
  externalInputSpecifier: ExternalInputSpecifierType | null
): Array<{batchId: string | null; containerId: string | null; fileId: string | null}> {
  if (externalInputSpecifier == null) {
    return [];
  }
  if (externalInputSpecifier.batchInfo) {
    return [{batchId: externalInputSpecifier.batchInfo.id, containerId: null, fileId: null}];
  } else if (externalInputSpecifier.containerInfo && externalInputSpecifier.containedItemsForContainer) {
    return externalInputSpecifier.containedItemsForContainer.map((containedItem) => {
      let batchId = null;
      let bioentityId = null;
      if (containedItem.id != null && APIUtil.isBiologicalEntity(containedItem.id)) {
        bioentityId = containedItem.id;
      } else {
        assert(
          containedItem.id != null && APIUtil.apiIdToType(containedItem.id) === ModelTypeConstants.BATCH,
          'Expected batch'
        );
        batchId = containedItem.id;
      }
      return {
        batchId,
        containerId: externalInputSpecifier.containerInfo!.id,
        fileId: bioentityId,
      };
    });
  } else if (externalInputSpecifier.bioentityInfo) {
    return [{batchId: null, containerId: null, fileId: externalInputSpecifier.bioentityInfo.id}];
  } else {
    return [];
  }
}

interface ExternalInputPickerProps {
  externalInputSpecifier: ExternalInputSpecifierType | null;
  initialInputType?: InputType | null;
  allowContainers: boolean;
  onSetExternalInputSpecifier: (externalInputSpecifier: ExternalInputSpecifierType) => void;
}

interface State {
  selectedInputType: string | null;
}

/**
 * Component that allows you to pick a batch or container, and when picking a container allows you
 * to select contained items from that container.
 *
 * State is encapsulated in an opaque externalInputSpecifier value. The parent should maintain and
 * pass in this value, and can call getInputSamplesFromSpecifier to resolve the specifier to the
 * corresponding input samples.
 */
export default class ExternalInputPicker extends React.Component<ExternalInputPickerProps, State> {
  constructor(props: ExternalInputPickerProps) {
    super(props);
    this.state = {
      selectedInputType: props.initialInputType || null,
    };
  }

  /**
   * Similar to setState, update a specific set of fields in the externalInputSpecifier, keeping
   * existing externalInputSpecifier values or falling back to defaults if we don't have one.
   */
  _updateSpecifier(newFields: Partial<ExternalInputSpecifierType>): void {
    const newExternalInputSpecifierType: ExternalInputSpecifierType = {
      ...makeEmptySampleDetails(),
      isContainable: null,
      containedItemsForContainer: null,
      ...(this.props.externalInputSpecifier || {}),
      ...newFields,
    };
    this.props.onSetExternalInputSpecifier(newExternalInputSpecifierType);
  }

  _handleSetSelectedInputType = (selectedInputType: InputType): void => {
    this.setState({selectedInputType});
    this._updateSpecifier({
      ...makeEmptySampleDetails(),
      isContainable: null,
      containedItemsForContainer: null,
    });
    preferences.set(WorkflowPreferences.EXTERNAL_INPUT_PICKER_INPUT_TYPE, selectedInputType);
  };

  _handleSetBatch = (batches: Array<Batch>): void => {
    const batch = batches.length ? batches[0] : null;
    const batchInfo =
      batch == null
        ? null
        : {
            id: batch.id,
            name: batch.name,
            fileId: batch.file.id,
            iconId: batch.file.iconId,
          };
    this._updateSpecifier({
      batchInfo,
      isContainable: true,
      containedItemsForContainer: null,
    });
  };

  _handleSetContainer = (containers: Array<ContainerInfo>): void => {
    this._updateSpecifier({
      containerInfo: containers.length ? containers[0] : null,
    });
  };

  _handleSetFile = (files: Array<BioentitySummary>): void => {
    const file = files.length ? files[0] : null;
    const isContainable = file && entityIsContainable(file);
    this._updateSpecifier({
      bioentityInfo: file
        ? {
            id: file.id,
            name: file.name,
          }
        : null,
      isContainable,
    });
  };

  _handleSetContainedItemsForContainer = (containedItems: Array<BioentitySummary | Batch>): void => {
    this._updateSpecifier({
      containedItemsForContainer: containedItems.map((item) => ({
        id: item.id,
        name: item.name,
        // eslint-disable-next-line benchlint/no-in-operator
        fileId: 'file' in item && item.file && item.file.id,
        // eslint-disable-next-line benchlint/no-in-operator
        iconId: 'file' in item && item.file && item.file.iconId,
      })),
    });
  };

  _getSelectedInputType(): InputType {
    const selectedInputType =
      this.state.selectedInputType ||
      preferences.get(WorkflowPreferences.EXTERNAL_INPUT_PICKER_INPUT_TYPE) ||
      InputType.FILE;
    if (selectedInputType === InputType.BATCH && !BenchlingConfig.ENABLE_BATCHES) {
      return InputType.FILE;
    }
    if (selectedInputType === InputType.CONTAINER && !this.props.allowContainers) {
      return InputType.FILE;
    }
    return selectedInputType;
  }

  _renderSearchInput(): JSX.Element {
    let batch = null;
    let container = null;
    let file = null;
    if (this.props.externalInputSpecifier) {
      batch = this.props.externalInputSpecifier.batchInfo;
      container = this.props.externalInputSpecifier.containerInfo;
      file = this.props.externalInputSpecifier.bioentityInfo;
    }
    const selectedInputType = this._getSelectedInputType();

    if (selectedInputType === InputType.BATCH) {
      return (
        <BatchSearchInput
          onChange={this._handleSetBatch}
          selectedBatches={batch ? [batch] : []}
          autoFocus={false}
        />
      );
    } else if (selectedInputType === InputType.CONTAINER) {
      return (
        <StorableSearchInput<ContainerInfo | Item>
          onChange={this._handleSetContainer}
          registryId={null}
          selectedStorables={container ? [container] : []}
          storableTypes={[ModelTypeConstants.CONTAINER]}
          autoFocus={false}
        />
      );
    } else {
      return (
        <BioentitySearchInput
          autoFocus={false}
          onChange={this._handleSetFile}
          selectedItems={file ? [file] : []}
        />
      );
    }
  }

  getRadioButtonOptions(): Array<{label: string; value: string}> {
    const options = [
      {
        label: 'Entity',
        value: InputType.FILE,
        dataTestAttrs: {component: 'ExternalInputPicker', key: InputType.FILE},
      },
    ];
    if (BenchlingConfig.ENABLE_BATCHES) {
      options.push({
        label: 'Batch',
        value: InputType.BATCH,
        dataTestAttrs: {component: 'ExternalInputPicker', key: InputType.BATCH},
      });
    }
    if (this.props.allowContainers) {
      options.push({
        label: 'Container',
        value: InputType.CONTAINER,
        dataTestAttrs: {component: 'ExternalInputPicker', key: InputType.CONTAINER},
      });
    }
    return options;
  }

  render(): JSX.Element {
    let container = null;
    let selectedContainedItemIds = null;
    if (this.props.externalInputSpecifier != null) {
      container = this.props.externalInputSpecifier.containerInfo;
      if (this.props.externalInputSpecifier.containedItemsForContainer) {
        selectedContainedItemIds = [];
        for (const item of this.props.externalInputSpecifier.containedItemsForContainer) {
          if (item.id != null) {
            selectedContainedItemIds.push(item.id);
          }
        }
      }
    }
    const selectedInputType = this._getSelectedInputType();

    return (
      <div {...getTestProps({component: 'ExternalInputPicker', element: 'root'})}>
        <RadioButtonSelector
          selectedValue={selectedInputType}
          options={this.getRadioButtonOptions()}
          onChange={this._handleSetSelectedInputType}
        />
        <div className="u-small-spacing-top">{this._renderSearchInput()}</div>
        {container && (
          <PickContentsFromContainerTable
            containerId={container.id}
            selectedContainedItemIds={selectedContainedItemIds}
            onSelectContainedItems={this._handleSetContainedItemsForContainer}
          />
        )}
      </div>
    );
  }
}
